/**
 * Zenith 360 · Portal Feature Selector (Admin)
 *
 * Iter-155 · A single place for the Business Owner / Admin to switch every
 * sidebar module ON/OFF per company. Sections here mirror the sidebar 1:1;
 * flipping any switch immediately hides that link in the left navigation
 * (and the corresponding top tab, where applicable).
 *
 * Data is stored on `organizations.portal_feature_toggles` as a flat
 * `{ key: bool }` dict, read via `GET /admin/portal-features`, written via
 * `PATCH /admin/portal-features` (business_owner / admin only).
 *
 * The panel groups features by section (Sales · Procurement · Inventory …)
 * with a section-level Enable-All / Disable-All shortcut, plus a live search
 * for fast finding.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Save, Search, ToggleLeft, ToggleRight, Sparkles, Info, Undo2, RotateCcw, UserCog,
} from "lucide-react";

/*
 * Master feature list — mirrors /app/frontend/src/components/layout/Sidebar.jsx
 * SECTIONS. Kept intentionally verbose (label + note per row) so admins can
 * self-serve without asking support.
 */
const CATALOG = [
  { section: "Overview", core: true, items: [
    { key: "dashboard",         label: "Dashboard",              note: "Home widgets + KPI cards", locked: true },
    { key: "ai_assistant",      label: "AI Assistant",           note: "Chat + doc Q&A across the workspace" },
    { key: "support_tickets",   label: "Support Tickets",        note: "Raise + track help tickets" },
  ]},
  { section: "Sales", items: [
    { key: "sales_wf",          label: "Sales Workflow",         note: "End-to-end SO → invoice funnel" },
    { key: "sales",             label: "Sales Invoices",         note: "Tax invoices, credit terms, e-invoicing" },
    { key: "credit_notes",      label: "Credit Notes",           note: "Refund + returns" },
    { key: "customers",         label: "Customers",              note: "Party master (customer type)" },
    { key: "recurring",         label: "Recurring Invoices",     note: "Subscription billing automation" },
    { key: "distribution",      label: "Distribution",           note: "Multi-warehouse dispatch orchestration" },
    { key: "gate_pass_outward", label: "Outward Gate Pass",      note: "Security-signed dispatch pass" },
  ]},
  { section: "Procurement", items: [
    { key: "purchase_wf",       label: "Purchase Workflow",      note: "PO → GRN → bill lifecycle" },
    { key: "purchases",         label: "Purchase Invoices",      note: "Vendor bills with ITC tracking" },
    { key: "purchase_return",   label: "Purchase Returns",       note: "Debit-note-worthy returns" },
    { key: "debit_notes",       label: "Debit Notes",            note: "GSTR-aware vendor debits" },
    { key: "vendors",           label: "Vendors",                note: "Party master (vendor type)" },
    { key: "gate_pass_inward",  label: "Inward Gate Pass",       note: "Security-signed receipt pass" },
    { key: "gate_inward",       label: "Gate Inward Register",   note: "Security post desk log" },
    { key: "stores_queue",      label: "Stores Queue",           note: "GRN acceptance + rejection" },
  ]},
  { section: "Inventory", items: [
    { key: "inventory",         label: "Item Master + Stock",    note: "SKUs, batches, warehouses, BIN" },
  ]},
  { section: "Manufacturing", items: [
    { key: "bom",               label: "Bill of Materials",      note: "Versioned BOMs with revisions" },
    { key: "bom_issue",         label: "BOM Issue",              note: "Raw material issue vouchers" },
    { key: "bom_receive",       label: "BOM Receive",            note: "Finished-goods receipts" },
    { key: "mfg_masters",       label: "MFG Masters",            note: "Work centres · routes · operations" },
    { key: "production",        label: "Production Orders",      note: "Plan → issue → complete" },
    { key: "mrp",               label: "MRP",                    note: "Material requirement planner" },
    { key: "maintenance",       label: "Maintenance",            note: "Preventive maintenance schedules" },
    { key: "qc_dashboard",      label: "QC Dashboard",           note: "Batch quality overview" },
    { key: "quality",           label: "Quality Module",         note: "Inspection plans + NCR" },
    { key: "procurement",       label: "Procurement Module",     note: "Structured PR / RFQ flow" },
    { key: "costing",           label: "Costing",                note: "Standard vs actual costing" },
    { key: "npd",               label: "NPD",                    note: "New-product development pipeline" },
    { key: "shopfloor",         label: "Shopfloor",              note: "Operator-facing screens" },
    { key: "andon",             label: "Andon",                  note: "Live shopfloor alerts" },
  ]},
  { section: "Dealership 360", items: [
    { key: "dealership",        label: "Dealership Module",      note: "Master switch (Leads → Delivery)" },
    { key: "van_sales",         label: "Van Sales",              note: "Beat plans · route sales" },
  ]},
  { section: "Job Work", items: [
    { key: "job_work",          label: "Job Work",               note: "Sub-contract lifecycle" },
    { key: "gate_pass_jobwork", label: "Job Work Gate Pass",     note: "Movement pass for outward JW stock" },
  ]},
  { section: "Banking", items: [
    { key: "payments",          label: "Payments",               note: "Cash / bank payment vouchers" },
    { key: "payment_sheet",     label: "Payment Approval Sheet", note: "Batch approve + release" },
    { key: "reconcile",         label: "Bank Reconcile",         note: "Statement upload + smart-match" },
    { key: "bank_rules",        label: "Bank Rules",             note: "Auto-classify recurring txns" },
    { key: "brs_review",        label: "BRS Review",             note: "Reconciliation exception queue" },
  ]},
  { section: "Accounting", items: [
    { key: "accounts",          label: "Chart of Accounts",      note: "Ledgers + groups + opening balances" },
    { key: "vouchers",          label: "Vouchers",               note: "Journal / receipt / payment entries" },
    { key: "prepaid",           label: "Prepaid Wizard",         note: "Amortisation schedules" },
    { key: "fixed_assets",      label: "Fixed Assets",           note: "Depreciation · Sch II + IT block" },
    { key: "approvals",         label: "Approvals",              note: "Payment · Discount · Rate approval inbox" },
  ]},
  { section: "GST & Returns", items: [
    { key: "gst_reports",       label: "GST Reports Hub",        note: "GSTR liability + ITC dashboards" },
    { key: "gstr1",             label: "GSTR-1",                 note: "Outward supplies filing" },
    { key: "ims",               label: "IMS",                    note: "Invoice Management System (accept/reject)" },
    { key: "gstr3b",            label: "GSTR-3B",                note: "Monthly summary filing" },
    { key: "gstr6_compliance",  label: "GSTR-6 (ISD)",           note: "Input service distributor" },
    { key: "gstr9_compliance",  label: "GSTR-9",                 note: "Annual return" },
    { key: "gstr9c_compliance", label: "GSTR-9C",                note: "Reconciliation statement" },
    { key: "itc04_compliance",  label: "ITC-04",                 note: "Job work quarterly return" },
    { key: "rcm_register",      label: "RCM Register",           note: "Reverse charge transactions" },
    { key: "rule37_reversal",   label: "Rule 37 Reversal",       note: "180-day ITC reversal automation" },
    { key: "ineligible_itc",    label: "Ineligible ITC",         note: "Blocked ITC register" },
    { key: "group_filing_dashboard", label: "Group Filing Dashboard", note: "Multi-GSTIN filing radar" },
  ]},
  { section: "Other Compliance", items: [
    { key: "compliance_hub",    label: "Compliance Hub",         note: "Central compliance overview" },
    { key: "tds",               label: "TDS",                    note: "26Q / 24Q · challans · returns" },
    { key: "tax_audit",         label: "Tax Audit (3CD)",        note: "Automated 3CD prep" },
    { key: "itr",               label: "Income Tax Return",      note: "ITR pre-fill + JSON generator" },
    { key: "corporate_compliance", label: "Corporate Compliance", note: "MCA · Form-8 · AOC-4 · DPT-3" },
    { key: "o2c_compliance",    label: "O2C Compliance",         note: "Order-to-cash regulatory guard" },
    { key: "compliance_calendar", label: "Compliance Calendar",  note: "Due-date radar + reminders" },
  ]},
  { section: "Reports & MIS", items: [
    { key: "reports",           label: "Reports",                note: "P&L · BS · Cash flow · trial balance" },
    { key: "statement_of_account", label: "Statement of Account", note: "Party-wise SoA" },
    { key: "segments",          label: "Segments",               note: "Cost-centre / project segmentation" },
    { key: "segment_mis",       label: "Segment MIS",            note: "Segment × metric pivot" },
    { key: "mis_pack",          label: "MIS Pack",               note: "One-click board pack" },
    { key: "consolidation",     label: "Consolidation",          note: "Group consolidation" },
    { key: "advance_tax",       label: "Advance Tax",            note: "Quarterly advance tax planner" },
    { key: "budgets",           label: "Budgets",                note: "Ledger-wise budget vs actual" },
    { key: "msme_report",       label: "MSME Report",            note: "43B(h) 45-day payable radar" },
    { key: "fema_realisation",  label: "FEMA Realisation",       note: "Export proceeds tracking" },
    { key: "as11_restatement",  label: "AS-11 Restatement",      note: "Forex monetary revaluation" },
    { key: "working_capital",   label: "Working-Capital Simulator", note: "OCC / CC forecasting" },
    { key: "fa_simulator",      label: "Fixed-Asset Simulator",  note: "What-if depreciation" },
  ]},
  { section: "Company & Setup", core: true, items: [
    { key: "settings",          label: "Settings",               note: "Company · branch · numbering", locked: true },
    { key: "user_guide",        label: "User Guide",             note: "In-app searchable docs" },
    { key: "subscription",      label: "Subscription",           note: "Plan · seats · billing", locked: true },
    { key: "company_gstins",    label: "Multi-GSTIN",            note: "Additional-place-of-business GSTINs" },
    { key: "tally_migrator",    label: "Tally Migrator",         note: "Import from Tally Prime" },
    { key: "zoho_migrator",     label: "Zoho Migrator",          note: "Import from Zoho Books" },
    { key: "migrator_mapper",   label: "Universal Mapper",       note: "Map any CSV → Customer / Vendor / Voucher" },
    { key: "import",            label: "Data Import",            note: "CSV / Excel bulk uploads" },
    { key: "templates",         label: "Templates",              note: "Invoice / voucher print templates" },
    { key: "audit",             label: "Audit Log",              note: "Immutable change history" },
    { key: "portal_features",   label: "Portal Feature Toggles", note: "This screen", locked: true },
  ]},
];

// Flatten to a lookup { key: item } for quick access
const FLAT_ITEMS = CATALOG.flatMap((s) => s.items);

// Iter-156 · Roles that can be overridden per-feature. Business Owner &
// Platform Admin are intentionally omitted — they always see everything.
const ROLES = [
  { key: "admin",          label: "Admin" },
  { key: "manager",        label: "Manager" },
  { key: "accountant",     label: "Accountant" },
  { key: "sales_exec",     label: "Sales Executive" },
  { key: "purchase_exec",  label: "Purchase Executive" },
  { key: "warehouse",      label: "Warehouse" },
  { key: "production",     label: "Production" },
  { key: "quality",        label: "Quality" },
  { key: "compliance",     label: "Compliance Officer" },
  { key: "auditor",        label: "Auditor (read-only)" },
  { key: "cashier",        label: "Cashier" },
  { key: "service_advisor", label: "Service Advisor" },
];

export default function PortalFeaturesPanel() {
  const [tab, setTab] = useState("portal");
  return (
    <div className="space-y-4" data-testid="portal-features-panel">
      <PageHeader
        eyebrow="admin · settings"
        title="Portal Feature Toggles"
        description="Select which modules are visible to users of this company. Data is never deleted — flip a switch back ON any time to bring history back."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="portal" className="rounded-sm" data-testid="pf-tab-portal">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Portal-wide
          </TabsTrigger>
          <TabsTrigger value="role" className="rounded-sm" data-testid="pf-tab-role">
            <UserCog className="w-3.5 h-3.5 mr-1" /> Per Role
          </TabsTrigger>
        </TabsList>
        <TabsContent value="portal" className="mt-3"><PortalWidePanel /></TabsContent>
        <TabsContent value="role"   className="mt-3"><RolePanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Portal-wide panel (existing) ============
function PortalWidePanel() {
  const [toggles, setToggles] = useState(null);   // {key: bool}
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/portal-features");
      const data = r.data || {};
      setToggles(data);
      setInitial(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => JSON.stringify(toggles) !== JSON.stringify(initial), [toggles, initial]);

  const flip = (key, v) => setToggles({ ...(toggles || {}), [key]: v });
  const sectionAction = (section, on) => {
    const next = { ...(toggles || {}) };
    section.items.forEach((it) => { if (!it.locked) next[it.key] = on; });
    setToggles(next);
  };
  const revert = () => setToggles(initial);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/portal-features", toggles || {});
      toast.success("Feature toggles saved — features hidden and blocked immediately");
      setInitial(toggles);
      // Iter-170 · Broadcast so Sidebar + FeatureGate refetch instantly
      window.dispatchEvent(new CustomEvent("feature-toggles-updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const onCount = useMemo(() => FLAT_ITEMS.filter((it) => (toggles || {})[it.key] !== false).length, [toggles]);
  const offCount = useMemo(() => FLAT_ITEMS.filter((it) => (toggles || {})[it.key] === false).length, [toggles]);

  const filteredCatalog = useMemo(() => {
    if (!search.trim()) return CATALOG;
    const q = search.toLowerCase();
    return CATALOG.map((s) => ({
      ...s,
      items: s.items.filter((it) => it.label.toLowerCase().includes(q) || it.key.includes(q) || (it.note || "").toLowerCase().includes(q)),
    })).filter((s) => s.items.length);
  }, [search]);

  if (!toggles) return <div className="text-muted-foreground p-6">Loading portal features…</div>;

  return (
    <div className="space-y-4" data-testid="portal-wide-subpanel">

      {/* KPI + save bar */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Enabled</div>
          <div className="font-heading text-2xl text-emerald-600 tabular-nums" data-testid="pf-count-on">{onCount}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Hidden</div>
          <div className="font-heading text-2xl text-red-500 tabular-nums" data-testid="pf-count-off">{offCount}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total features</div>
          <div className="font-heading text-2xl tabular-nums">{FLAT_ITEMS.length}</div>
        </Card>
        <Card className="rounded-sm p-3 flex items-center gap-2">
          <Button onClick={save} disabled={!dirty || busy}
            className="rounded-sm bg-accent hover:bg-accent/90 text-foreground gap-1.5 flex-1" data-testid="pf-save">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
          {dirty && (
            <Button onClick={revert} variant="ghost" size="sm" className="rounded-sm h-8 gap-1 text-muted-foreground" data-testid="pf-revert">
              <Undo2 className="w-3.5 h-3.5" /> Revert
            </Button>
          )}
        </Card>
      </div>

      {/* Info banner */}
      <div className="border rounded-sm p-3 bg-blue-50/50 text-[11px] text-blue-900 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Turning a feature OFF hides it from the sidebar and top-tab bars <b>only</b>. Nothing is deleted; historic data remains queryable via reports and audit log.
          Some core screens (<span className="font-mono">dashboard</span>, <span className="font-mono">settings</span>, <span className="font-mono">subscription</span>, <span className="font-mono">portal_features</span>) are locked ON so you never brick your own portal.
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter features by name or key…" className="pl-8 rounded-sm h-9" data-testid="pf-search" />
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {filteredCatalog.map((section) => {
          const secOn = section.items.every((it) => (toggles || {})[it.key] !== false);
          const secOff = section.items.every((it) => (toggles || {})[it.key] === false);
          return (
            <Card key={section.section} className="rounded-sm overflow-hidden" data-testid={`pf-section-${section.section.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
              <div className="flex items-center gap-3 p-3 bg-slate-50 border-b">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex-1 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> {section.section}
                  {section.core && <span className="text-[9px] font-mono uppercase text-amber-600">core</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => sectionAction(section, true)} disabled={secOn}
                    className="h-7 rounded-sm text-[10px] gap-1 text-emerald-700" data-testid={`pf-section-on-${section.section.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
                    <ToggleRight className="w-3 h-3" /> Enable all
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => sectionAction(section, false)} disabled={secOff}
                    className="h-7 rounded-sm text-[10px] gap-1 text-red-600" data-testid={`pf-section-off-${section.section.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
                    <ToggleLeft className="w-3 h-3" /> Disable all
                  </Button>
                </div>
              </div>
              <div className="divide-y">
                {section.items.map((it) => {
                  const on = (toggles || {})[it.key] !== false;
                  return (
                    <div key={it.key} className="flex items-center gap-3 p-3 hover:bg-slate-50/40" data-testid={`pf-row-${it.key}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-2">
                          {it.label}
                          {it.locked && <span className="text-[9px] font-mono uppercase text-amber-600" title="Locked ON for platform safety">locked</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                          <span className="font-mono">{it.key}</span> — {it.note}
                        </div>
                      </div>
                      <Switch checked={on} disabled={it.locked}
                        onCheckedChange={(v) => flip(it.key, v)} data-testid={`pf-toggle-${it.key}`} />
                      <div className="w-14 text-right text-[10px] font-mono uppercase">
                        {on ? <span className="text-emerald-600">ON</span> : <span className="text-red-500">OFF</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ============ Role-based panel (new · Iter-156) ============
function RolePanel() {
  const [selectedRole, setSelectedRole] = useState(ROLES[2].key); // default: accountant
  const [portalToggles, setPortalToggles] = useState({});
  const [roleToggles, setRoleToggles] = useState({});      // {role: {key: bool}}
  const [initialRoleToggles, setInitialRoleToggles] = useState({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [p, r] = await Promise.all([
        api.get("/admin/portal-features"),
        api.get("/admin/role-feature-toggles"),
      ]);
      setPortalToggles(p.data || {});
      setRoleToggles(r.data || {});
      setInitialRoleToggles(r.data || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const currentRoleMap = roleToggles[selectedRole] || {};
  const initialRoleMap = initialRoleToggles[selectedRole] || {};
  const dirty = useMemo(
    () => JSON.stringify(currentRoleMap) !== JSON.stringify(initialRoleMap),
    [currentRoleMap, initialRoleMap]);

  const flip = (key, v) => {
    setRoleToggles({
      ...roleToggles,
      [selectedRole]: { ...(roleToggles[selectedRole] || {}), [key]: v },
    });
  };
  const sectionAction = (section, on) => {
    const next = { ...(roleToggles[selectedRole] || {}) };
    section.items.forEach((it) => {
      if (!it.locked && portalToggles[it.key] !== false) next[it.key] = on;
    });
    setRoleToggles({ ...roleToggles, [selectedRole]: next });
  };
  const revert = () => setRoleToggles(initialRoleToggles);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/role-feature-toggles",
        { [selectedRole]: roleToggles[selectedRole] || {} });
      toast.success(`Overrides for ${labelFor(selectedRole)} saved — hidden + blocked immediately`);
      setInitialRoleToggles(roleToggles);
      // Iter-170 · Broadcast so Sidebar + FeatureGate refetch instantly
      window.dispatchEvent(new CustomEvent("feature-toggles-updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const labelFor = (k) => (ROLES.find((r) => r.key === k) || {}).label || k;

  // Effective visibility for this role = portal_on AND role_on
  const effectiveOn = (key) => portalToggles[key] !== false && (currentRoleMap[key] !== false);

  const onCount = FLAT_ITEMS.filter((it) => effectiveOn(it.key)).length;
  const roleHiddenCount = FLAT_ITEMS.filter((it) => portalToggles[it.key] !== false && currentRoleMap[it.key] === false).length;
  const portalHiddenCount = FLAT_ITEMS.filter((it) => portalToggles[it.key] === false).length;

  const filteredCatalog = useMemo(() => {
    if (!search.trim()) return CATALOG;
    const q = search.toLowerCase();
    return CATALOG.map((s) => ({
      ...s,
      items: s.items.filter((it) => it.label.toLowerCase().includes(q) || it.key.includes(q) || (it.note || "").toLowerCase().includes(q)),
    })).filter((s) => s.items.length);
  }, [search]);

  return (
    <div className="space-y-4" data-testid="role-subpanel">

      {/* Role picker + save row */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="rounded-sm p-3 col-span-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Editing overrides for role</div>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="rounded-sm h-9" data-testid="pf-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r.key} value={r.key} data-testid={`pf-role-opt-${r.key}`}>
                  {r.label} <span className="opacity-50 font-mono ml-1">· {r.key}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Effective on for this role</div>
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-2xl text-emerald-600 tabular-nums" data-testid="pf-role-count-on">{onCount}</div>
            <div className="text-[10px] font-mono text-muted-foreground">of {FLAT_ITEMS.length}</div>
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            <span className="text-red-500">{roleHiddenCount}</span> hidden by role · <span className="text-red-500">{portalHiddenCount}</span> hidden portal-wide
          </div>
        </Card>
        <Card className="rounded-sm p-3 flex items-center gap-2">
          <Button onClick={save} disabled={!dirty || busy}
            className="rounded-sm bg-accent hover:bg-accent/90 text-foreground gap-1.5 flex-1" data-testid="pf-role-save">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : dirty ? "Save role" : "Saved"}
          </Button>
          {dirty && (
            <Button onClick={revert} variant="ghost" size="sm" className="rounded-sm h-8 gap-1 text-muted-foreground" data-testid="pf-role-revert">
              <Undo2 className="w-3.5 h-3.5" /> Revert
            </Button>
          )}
        </Card>
      </div>

      {/* Info banner */}
      <div className="border rounded-sm p-3 bg-purple-50/50 text-[11px] text-purple-900 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Role overrides can only <b>further restrict</b> what a role sees — they cannot re-enable something you've turned off portal-wide.
          Features already hidden by portal-wide toggles are shown as <span className="font-mono">portal-off</span> and cannot be flipped here.
          <b> Business Owner and Platform Admin roles always see everything</b> (no per-role overrides apply to them).
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter features by name or key…" className="pl-8 rounded-sm h-9" data-testid="pf-role-search" />
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {filteredCatalog.map((section) => {
          return (
            <Card key={section.section} className="rounded-sm overflow-hidden" data-testid={`pf-role-section-${section.section.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
              <div className="flex items-center gap-3 p-3 bg-slate-50 border-b">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex-1 flex items-center gap-2">
                  <UserCog className="w-3 h-3" /> {section.section}
                  {section.core && <span className="text-[9px] font-mono uppercase text-amber-600">core</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => sectionAction(section, true)}
                    className="h-7 rounded-sm text-[10px] gap-1 text-emerald-700" data-testid={`pf-role-section-on-${section.section.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
                    <ToggleRight className="w-3 h-3" /> Show all for role
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => sectionAction(section, false)}
                    className="h-7 rounded-sm text-[10px] gap-1 text-red-600" data-testid={`pf-role-section-off-${section.section.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
                    <ToggleLeft className="w-3 h-3" /> Hide all for role
                  </Button>
                </div>
              </div>
              <div className="divide-y">
                {section.items.map((it) => {
                  const portalOff = portalToggles[it.key] === false;
                  const roleOn = (currentRoleMap[it.key] ?? true) === true;
                  const effective = !portalOff && roleOn;
                  return (
                    <div key={it.key} className={`flex items-center gap-3 p-3 hover:bg-slate-50/40 ${portalOff ? "opacity-50" : ""}`} data-testid={`pf-role-row-${it.key}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-2">
                          {it.label}
                          {it.locked && <span className="text-[9px] font-mono uppercase text-amber-600">locked</span>}
                          {portalOff && <span className="text-[9px] font-mono uppercase text-red-500">portal-off</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                          <span className="font-mono">{it.key}</span> — {it.note}
                        </div>
                      </div>
                      <Switch
                        checked={roleOn}
                        disabled={it.locked || portalOff}
                        onCheckedChange={(v) => flip(it.key, v)}
                        data-testid={`pf-role-toggle-${it.key}`}
                      />
                      <div className="w-16 text-right text-[10px] font-mono uppercase">
                        {portalOff ? <span className="text-muted-foreground">n/a</span>
                          : effective ? <span className="text-emerald-600">visible</span>
                          : <span className="text-red-500">hidden</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
